import { Copy, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export const CLONE_TITLE_MAX_LENGTH = 100;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  onTitleChange: (title: string) => void;
  sourceTitle: string;
  isEvergreen: boolean;
  isCloning: boolean;
  isLocked: boolean;
  onConfirm: () => void;
};

export default function CloneWebinarDialog({
  open,
  onOpenChange,
  title,
  onTitleChange,
  sourceTitle,
  isEvergreen,
  isCloning,
  isLocked,
  onConfirm,
}: Props) {
  const { t } = useTranslation('webinars');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <button className="flex items-center gap-2 cursor-pointer">
          <Copy size={24} className="text-secondary" />
          <p className="text-sm font-medium text-secondary">{t('sessionPage.clone.action')}</p>
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('sessionPage.clone.confirmTitle')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <div>{t('sessionPage.clone.warning1')}</div>
          {isEvergreen && <div>{t('sessionPage.clone.evergreenContent')}</div>}
          <div>{t('sessionPage.clone.warning2')}</div>
          <div className="space-y-1 mt-4">
            <Label htmlFor="clone-webinar-title">{t('sessionPage.clone.titleLabel')}</Label>
            <Input
              id="clone-webinar-title"
              value={title}
              onChange={(event) => onTitleChange(event.target.value)}
              placeholder={t('sessionPage.clone.titlePlaceholder', { title: sourceTitle })}
              maxLength={CLONE_TITLE_MAX_LENGTH}
              disabled={isCloning}
              className="h-12"
            />
            <p
              className={`text-xs text-right ${
                title.length > CLONE_TITLE_MAX_LENGTH ? 'text-error' : 'text-secondary'
              }`}
            >
              {t('sessionPage.clone.charactersCount', {
                current: title.length,
                max: CLONE_TITLE_MAX_LENGTH,
              })}
            </p>
          </div>
          {isCloning && (
            <div className="flex items-center gap-2 text-sm text-secondary">
              <Loader2 size={16} className="animate-spin" />
              <span>{t('sessionPage.clone.inProgress')}</span>
            </div>
          )}
        </div>
        <DialogFooter className="w-full flex justify-between gap-4">
          <DialogTrigger asChild>
            <div className="w-1/2">
              <Button variant="outline" className="w-full" disabled={isCloning}>
                {t('sessionPage.cancel')}
              </Button>
            </div>
          </DialogTrigger>
          <div className="w-1/2">
            <Button
              onClick={onConfirm}
              className="w-full"
              disabled={isCloning || isLocked || !title.trim()}
            >
              {(isCloning || isLocked) && <Loader2 className="animate-spin" />}
              {t('sessionPage.clone.confirmButton')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
